import { Component } from '@theme/component';

class BarryLoopEntry extends Component {
  connectedCallback() { super.connectedCallback(); this.setup(); }
  updatedCallback() { this.cleanup(); super.updatedCallback(); this.setup(); }
  setup() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.video = this.querySelector('video'); this.visual = this.querySelector('[data-loop-link]');
    this.toggle = this.querySelector('[data-loop-toggle]'); this.caption = this.querySelector('[data-loop-caption]');
    this.captionLink = this.caption?.closest('a'); this.credits = this.querySelector('[data-loop-credit]');
    this.quietLinks = this.querySelector('[data-loop-projects]'); this.overlay = this.querySelector('[data-opening-overlay]');
    if (!this.video || !this.visual || !this.toggle) return;
    try { this.cues = JSON.parse(this.querySelector('[data-loop-cues]')?.textContent || '[]'); } catch { this.cues = []; }
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)'); this.portrait = matchMedia('(max-aspect-ratio: 1/1)'); this.connection = navigator.connection;
    this.explicit = false; this.paused = false; this.failed = false; this.sourceReady = false; this.hasPlayed = false; this.firstCredit = false; this.introActive = false; this.timers = [];
    const rect = this.querySelector('.bc-loop-stage').getBoundingClientRect(); this.visible = rect.bottom > 0 && rect.top < innerHeight;
    this.toggle.hidden = false;
    this.toggle.addEventListener('click', () => {
      this.finishOpening('interaction');
      if (this.failed || this.quiet()) { this.failed = false; this.explicit = true; this.paused = false; }
      else this.paused = !this.paused;
      this.sync();
    }, options);
    for (const link of [this.visual, this.captionLink].filter(Boolean)) {
      link.addEventListener('pointerdown', () => this.updateLink(), options);
      link.addEventListener('click', () => { this.updateLink(); this.video.pause(); }, options);
      link.addEventListener('focus', () => { this.updateLink(); if (!this.video.paused) { this.paused = true; this.sync(); } }, options);
    }
    this.video.addEventListener('loadedmetadata', () => {
      if (!this.video.hasAttribute('src')) return;
      const target = this.resumeTime ?? (this.hasPlayed ? 0 : 3);
      this.resumeTime = null;
      const ready = () => { this.sourceReady = true; this.sync(); };
      if (Math.abs(this.video.currentTime - target) < .02) ready();
      else { this.video.addEventListener('seeked', ready, { ...options, once: true }); this.video.currentTime = target; }
    }, options);
    this.video.addEventListener('playing', () => {
      if (!this.sourceReady) return;
      this.classList.add('is-playing');
      if (!this.hasPlayed) {
        this.hasPlayed = true; this.firstCredit = true; this.creditEnd = this.video.currentTime + 3;
        this.dataset.firstPlayingTime = this.video.currentTime.toFixed(3);
      }
      if (this.introActive) this.dataset.playingDuringOpening = 'true';
      this.updateLink();
    }, options);
    this.video.addEventListener('timeupdate', () => this.updateLink(), options);
    this.video.addEventListener('error', () => { this.failed = true; this.paused = true; this.finishOpening('video-error'); this.release(); this.sync(); }, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.finishOpening('hidden'); this.sync(); }, options);
    for (const name of ['pointerdown', 'keydown', 'wheel']) document.addEventListener(name, () => this.finishOpening('interaction'), { ...options, capture: true, passive: true });
    document.addEventListener('bc:opening-timeout', () => this.finishOpening('failsafe'), options);
    this.reduced.addEventListener('change', () => { if (this.reduced.matches) { this.explicit = false; this.finishOpening('reduced-motion'); } this.sync(); }, options);
    this.connection?.addEventListener('change', () => { if (this.quiet()) this.finishOpening('connection'); this.sync(); }, options);
    this.portrait.addEventListener('change', () => { this.finishOpening('orientation'); this.resumeTime = this.video.hasAttribute('src') ? this.video.currentTime : null; this.release(); this.sync(); }, options);
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, { threshold: .15 });
    this.observer.observe(this.querySelector('.bc-loop-stage'));
    let seen = false; try { seen = sessionStorage.getItem('bc-v68-opening') === '1'; } catch { /* Memory is optional. */ }
    if (this.dataset.intro === 'true' && this.dataset.editor !== 'true' && !location.hash && !this.quiet() && document.documentElement.dataset.v68OpeningExpired !== 'true' && (this.dataset.introFrequency === 'visit' || !seen)) this.startOpening();
    document.documentElement.classList.remove('bc-v68-pending'); this.sync();
  }
  quiet() { return !this.explicit && (this.reduced.matches || this.connection?.saveData || /(^|-)2g$|^3g$/.test(this.connection?.effectiveType || '')); }
  source() {
    const good = !this.connection || (!this.connection.saveData && (this.connection.effectiveType || '4g') === '4g' && (this.connection.downlink || 10) >= 5);
    if (this.portrait.matches) return (innerWidth >= 768 && good && this.dataset.portraitHdUrl) || this.dataset.portraitUrl;
    return (innerWidth >= 1200 && good && this.dataset.desktopHdUrl) || this.dataset.desktopUrl;
  }
  setToggle(label, playing = false) {
    this.toggle.setAttribute('aria-label', label); this.toggle.setAttribute('aria-pressed', String(playing));
    this.toggle.querySelector('span').textContent = label;
    this.toggle.querySelector('path').setAttribute('d', playing ? 'M3 2H6V14H3ZM10 2H13V14H10Z' : 'M4 2 14 8 4 14Z');
  }
  release() {
    this.video.pause(); this.sourceReady = false;
    if (this.video.hasAttribute('src')) { this.video.removeAttribute('src'); this.video.load(); }
    this.classList.remove('is-playing'); this.dataset.bookend = 'false';
  }
  sync() {
    const quiet = this.quiet(); this.dataset.quiet = String(Boolean(quiet)); if (this.quietLinks) this.quietLinks.hidden = !quiet;
    if (quiet || this.failed) { this.release(); this.setToggle(this.failed ? this.dataset.retry : this.dataset.play); this.updateLink(); return; }
    const allowed = this.visible && !document.hidden && !this.paused;
    this.setToggle(this.paused ? this.dataset.play : this.dataset.pause, !this.paused);
    if (!allowed) { this.video.pause(); return; }
    if (!this.video.hasAttribute('src')) {
      const source = this.source(); if (!source) { this.toggle.hidden = true; this.finishOpening('missing-video'); return; }
      this.sourceReady = false; this.video.preload = 'auto'; this.video.muted = true; this.video.src = source; this.video.load(); return;
    }
    if (!this.sourceReady || !this.video.paused || this.playPending) return;
    this.playPending = true;
    this.video.play().catch(error => {
      if (error.name === 'AbortError' || !this.isConnected) return;
      this.paused = true; this.setToggle(this.dataset.play); this.finishOpening('autoplay-unavailable');
    }).finally(() => { this.playPending = false; });
  }
  updateLink() {
    const time = this.video.currentTime, playing = this.classList.contains('is-playing');
    const cue = playing ? this.cues.find(c => time >= c.start && time < c.end) : null;
    if (this.firstCredit && (time >= this.creditEnd || time < 3)) this.firstCredit = false;
    const openingPhoto = !playing || this.firstCredit;
    const target = !playing ? this.dataset.posterProject : cue?.url?.startsWith('/pages/') ? cue.url : this.dataset.workUrl;
    this.visual.setAttribute('href', target);
    this.captionLink?.setAttribute('href', openingPhoto ? this.dataset.posterProject : target);
    this.caption.textContent = openingPhoto ? this.dataset.posterCaption : cue && cue.end - cue.start >= 1 ? cue.title + ' ↗' : this.dataset.summary;
    if (this.credits) this.credits.textContent = openingPhoto ? this.dataset.posterCredit : cue && cue.end - cue.start >= 1 ? [cue.role, cue.credit].filter(Boolean).join(' / ') : '';
    this.dataset.bookend = String(playing && (time < 3 || time >= 30));
  }
  later(callback, delay) { this.timers.push(setTimeout(() => { if (this.introActive) callback(); }, delay)); }
  startOpening() {
    if (!this.overlay || this.introActive || this.quiet()) return;
    this.introActive = true; this.openedAt = performance.now(); this.overlay.hidden = false;
    document.documentElement.classList.add('bc-v68-opening'); this.dataset.openingCopy = 'hidden';
    const mark = this.overlay.querySelector('svg'), plate = this.overlay.querySelector('[data-opening-plate]');
    const W = innerWidth, H = innerHeight, ratio = 1267 / 1258;
    const w = Math.min(W * .30, H * .32, 340), x = (W - w) / 2, y = (H - w * ratio) / 2;
    this.overlay.style.setProperty('--mark-width', `${w}px`); this.overlay.style.setProperty('--mark-x', `${x}px`); this.overlay.style.setProperty('--mark-y', `${y}px`);
    const path = mark.querySelector('path').getAttribute('d');
    const mask = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1258 1267"><path fill="black" fill-rule="evenodd" d="${path}"/></svg>`;
    plate.style.setProperty('--b-mask', `url("data:image/svg+xml,${encodeURIComponent(mask)}")`);
    plate.style.setProperty('--mask-size', `${w}px ${w * ratio}px`); plate.style.setProperty('--mask-position', `${x}px ${y}px`);
    plate.dataset.fallback = String(!(CSS.supports('mask-composite', 'exclude') || CSS.supports('-webkit-mask-composite', 'xor')));
    this.overlay.dataset.phase = 'solid';
    requestAnimationFrame(() => { if (this.introActive) this.overlay.classList.add('is-visible'); });
    this.sync(); // The reel seeks to 3.0 and plays behind the plate while it is still opaque.
    const reveal = () => {
      if (!this.introActive) return;
      const waited = performance.now() - this.openedAt;
      if ((this.video.paused || !this.classList.contains('is-playing')) && waited < 1100) { this.later(reveal, 40); return; }
      this.overlay.dataset.phase = 'window'; plate.classList.add('has-window');
      this.dataset.apertureVideoStart = this.video.currentTime.toFixed(3); this.dataset.aperturePlaying = String(!this.video.paused && this.classList.contains('is-playing'));
      const start = performance.now(), finalW = Math.hypot(W, H) * .62 / .1085, ax = .2528, ay = .5741;
      const grow = now => {
        if (!this.introActive) return;
        const p = Math.min(1, (now - start) / 850), e = p < .5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2;
        const size = w * (finalW / w) ** e, cx = x + w * ax + (W / 2 - (x + w * ax)) * e, cy = y + w * ratio * ay + (H / 2 - (y + w * ratio * ay)) * e;
        plate.style.setProperty('--mask-size', `${size}px ${size * ratio}px`); plate.style.setProperty('--mask-position', `${cx - size * ax}px ${cy - size * ratio * ay}px`);
        if (p >= .62) this.dataset.openingCopy = 'visible';
        if (p < 1) this.frame = requestAnimationFrame(grow); else this.finishOpening('complete');
      };
      this.frame = requestAnimationFrame(grow);
    };
    this.later(reveal, 650); this.later(() => this.finishOpening('cap'), 2250);
  }
  finishOpening(reason) {
    if (!this.introActive) return;
    this.introActive = false; this.timers.forEach(clearTimeout); this.timers = []; cancelAnimationFrame(this.frame);
    this.dataset.openingMs = (performance.now() - this.openedAt).toFixed(0); this.dataset.openingReason = reason; this.dataset.apertureVideoEnd = this.video.currentTime.toFixed(3);
    if (this.overlay) { this.overlay.hidden = true; this.overlay.classList.remove('is-visible'); }
    this.dataset.openingCopy = 'visible'; document.documentElement.classList.remove('bc-v68-pending', 'bc-v68-opening');
    try { sessionStorage.setItem('bc-v68-opening', '1'); } catch { /* Never required for access. */ }
  }
  cleanup() {
    this.abort?.abort(); this.observer?.disconnect(); this.timers?.forEach(clearTimeout); cancelAnimationFrame(this.frame); this.video?.pause(); this.introActive = false;
    document.documentElement.classList.remove('bc-v68-pending', 'bc-v68-opening');
  }
  disconnectedCallback() { this.cleanup(); super.disconnectedCallback(); }
}
if (!customElements.get('bc-loop-entry')) customElements.define('bc-loop-entry', BarryLoopEntry);
